/**
 * Command Executor for UI-Model-1 (API-Direct Approach)
 *
 * Executes commands against the UIModelState (API format).
 * Provides execute/undo capabilities for the command pattern.
 */

import { UIModelState } from '../types/state.types';
import {
  UndoableCommand,
  AddContainerCommand,
  RemoveContainerCommand,
  UpdateContainerCommand,
  MoveContainerCommand,
  ReorderContainersCommand,
  DuplicateContainerCommand,
  AddItemCommand,
  RemoveItemCommand,
  MoveItemCommand,
  UpdateItemCommand,
  ReorderItemsCommand,
  BatchCommand,
  UpdateHeaderCommand,
  UpdateFooterCommand,
  AddGroupCommand,
  AddPageCommand,
  UpdateGroupCommand,
  UpdatePageCommand,
  UpdateNavigationConfigCommand,
  RemoveGroupCommand,
  RemovePageCommand,
  ReorderGroupsCommand,
  ReorderPagesCommand,
  MovePageBetweenGroupsCommand,
  DuplicatePageCommand,
} from '../types/command.types';
import {
  executeAddContainer,
  executeRemoveContainer,
  executeUpdateContainer,
  executeMoveContainer,
  executeReorderContainers,
  executeDuplicateContainer,
  undoAddContainer,
  undoRemoveContainer,
  undoUpdateContainer,
  undoMoveContainer,
  undoReorderContainers,
  undoDuplicateContainer,
} from './implementations/containerCommands';
import {
  executeAddItem,
  executeRemoveItem,
  executeMoveItem,
  executeUpdateItem,
  executeReorderItems,
  undoAddItem,
  undoRemoveItem,
  undoMoveItem,
  undoUpdateItem,
  undoReorderItems,
} from './implementations/itemCommands';
import {
  executeUpdateHeader,
  undoUpdateHeader,
} from './implementations/headerCommands';
import {
  executeUpdateFooter,
  undoUpdateFooter,
} from './implementations/footerCommands';
import {
  executeAddGroup,
  undoAddGroup,
  executeAddPage,
  undoAddPage,
  executeUpdateGroup,
  undoUpdateGroup,
  executeUpdatePage,
  undoUpdatePage,
  executeUpdateNavigationConfig,
  undoUpdateNavigationConfig,
  executeRemoveGroup,
  undoRemoveGroup,
  executeRemovePage,
  undoRemovePage,
  executeReorderGroups,
  undoReorderGroups,
  executeReorderPages,
  undoReorderPages,
  executeMovePageBetweenGroups,
  undoMovePageBetweenGroups,
  executeDuplicatePage,
  undoDuplicatePage,
} from './implementations/navigationCommands';
import * as selectors from '../selectors';

// ============================================================================
// Command Executor
// ============================================================================

/**
 * Executes a command and returns the new state
 */
export const executeCommand = (
  state: UIModelState,
  command: UndoableCommand
): UIModelState => {
  switch (command.type) {
    case 'ADD_CONTAINER':
      return executeAddContainer(state, command as AddContainerCommand);

    case 'REMOVE_CONTAINER':
      return executeRemoveContainer(state, command as RemoveContainerCommand);

    case 'UPDATE_CONTAINER':
      return executeUpdateContainer(state, command as UpdateContainerCommand);

    case 'MOVE_CONTAINER':
      return executeMoveContainer(state, command as MoveContainerCommand);

    case 'REORDER_CONTAINERS':
      return executeReorderContainers(state, command as ReorderContainersCommand);

    case 'DUPLICATE_CONTAINER':
      return executeDuplicateContainer(state, command as DuplicateContainerCommand);

    case 'ADD_ITEM':
      return executeAddItem(state, command as AddItemCommand);

    case 'REMOVE_ITEM':
      return executeRemoveItem(state, command as RemoveItemCommand);

    case 'MOVE_ITEM':
      return executeMoveItem(state, command as MoveItemCommand);

    case 'UPDATE_ITEM':
      return executeUpdateItem(state, command as UpdateItemCommand);

    case 'REORDER_ITEMS':
      return executeReorderItems(state, command as ReorderItemsCommand);

    case 'UPDATE_HEADER':
      return executeUpdateHeader(state, command as UpdateHeaderCommand);

    case 'UPDATE_FOOTER':
      return executeUpdateFooter(state, command as UpdateFooterCommand);

    case 'ADD_GROUP':
      return executeAddGroup(state, command as AddGroupCommand);

    case 'ADD_PAGE':
      return executeAddPage(state, command as AddPageCommand);

    case 'UPDATE_GROUP':
      return executeUpdateGroup(state, command as UpdateGroupCommand);

    case 'UPDATE_PAGE':
      return executeUpdatePage(state, command as UpdatePageCommand);

    case 'UPDATE_NAVIGATION_CONFIG':
      return executeUpdateNavigationConfig(state, command as UpdateNavigationConfigCommand);

    case 'REMOVE_GROUP':
      return executeRemoveGroup(state, command as RemoveGroupCommand);

    case 'REMOVE_PAGE':
      return executeRemovePage(state, command as RemovePageCommand);

    case 'REORDER_GROUPS':
      return executeReorderGroups(state, command as ReorderGroupsCommand);

    case 'REORDER_PAGES':
      return executeReorderPages(state, command as ReorderPagesCommand);

    case 'MOVE_PAGE_BETWEEN_GROUPS':
      return executeMovePageBetweenGroups(state, command as MovePageBetweenGroupsCommand);

    case 'DUPLICATE_PAGE':
      return executeDuplicatePage(state, command as DuplicatePageCommand);

    case 'BATCH':
      return executeBatchCommand(state, command as BatchCommand);

    default:
      console.warn(`Unknown command type: ${(command as UndoableCommand).type}`);
      return state;
  }
};

/**
 * Undoes a command and returns the previous state
 */
export const undoCommand = (
  state: UIModelState,
  command: UndoableCommand
): UIModelState => {
  switch (command.type) {
    case 'ADD_CONTAINER':
      return undoAddContainer(state, command as AddContainerCommand);

    case 'REMOVE_CONTAINER':
      return undoRemoveContainer(state, command as RemoveContainerCommand);

    case 'UPDATE_CONTAINER':
      return undoUpdateContainer(state, command as UpdateContainerCommand);

    case 'MOVE_CONTAINER':
      return undoMoveContainer(state, command as MoveContainerCommand);

    case 'REORDER_CONTAINERS':
      return undoReorderContainers(state, command as ReorderContainersCommand);

    case 'DUPLICATE_CONTAINER':
      return undoDuplicateContainer(state, command as DuplicateContainerCommand);

    case 'ADD_ITEM':
      return undoAddItem(state, command as AddItemCommand);

    case 'REMOVE_ITEM':
      return undoRemoveItem(state, command as RemoveItemCommand);

    case 'MOVE_ITEM':
      return undoMoveItem(state, command as MoveItemCommand);

    case 'UPDATE_ITEM':
      return undoUpdateItem(state, command as UpdateItemCommand);

    case 'REORDER_ITEMS':
      return undoReorderItems(state, command as ReorderItemsCommand);

    case 'UPDATE_HEADER':
      return undoUpdateHeader(state, command as UpdateHeaderCommand);

    case 'UPDATE_FOOTER':
      return undoUpdateFooter(state, command as UpdateFooterCommand);

    case 'ADD_GROUP':
      return undoAddGroup(state, command as AddGroupCommand);

    case 'ADD_PAGE':
      return undoAddPage(state, command as AddPageCommand);

    case 'UPDATE_GROUP':
      return undoUpdateGroup(state, command as UpdateGroupCommand);

    case 'UPDATE_PAGE':
      return undoUpdatePage(state, command as UpdatePageCommand);

    case 'UPDATE_NAVIGATION_CONFIG':
      return undoUpdateNavigationConfig(state, command as UpdateNavigationConfigCommand);

    case 'REMOVE_GROUP':
      return undoRemoveGroup(state, command as RemoveGroupCommand);

    case 'REMOVE_PAGE':
      return undoRemovePage(state, command as RemovePageCommand);

    case 'REORDER_GROUPS':
      return undoReorderGroups(state, command as ReorderGroupsCommand);

    case 'REORDER_PAGES':
      return undoReorderPages(state, command as ReorderPagesCommand);

    case 'MOVE_PAGE_BETWEEN_GROUPS':
      return undoMovePageBetweenGroups(state, command as MovePageBetweenGroupsCommand);

    case 'DUPLICATE_PAGE':
      return undoDuplicatePage(state, command as DuplicatePageCommand);

    case 'BATCH':
      return undoBatchCommand(state, command as BatchCommand);

    default:
      console.warn(`Unknown command type for undo: ${(command as UndoableCommand).type}`);
      return state;
  }
};

// ============================================================================
// Batch Command Execution
// ============================================================================

/**
 * Executes a batch of commands in sequence
 */
const executeBatchCommand = (
  state: UIModelState,
  command: BatchCommand
): UIModelState => {
  return command.payload.commands.reduce(
    (currentState, subCommand) => executeCommand(currentState, subCommand),
    state
  );
};

/**
 * Undoes a batch of commands in reverse order
 */
const undoBatchCommand = (
  state: UIModelState,
  command: BatchCommand
): UIModelState => {
  // Undo in reverse order
  const reversedCommands = [...command.payload.commands].reverse();
  return reversedCommands.reduce(
    (currentState, subCommand) => undoCommand(currentState, subCommand),
    state
  );
};

// ============================================================================
// Command Validation
// ============================================================================

/**
 * Validates a command before execution
 * Uses selectors to traverse the nested API structure
 */
export const validateCommand = (
  state: UIModelState,
  command: UndoableCommand
): { valid: boolean; error?: string } => {
  switch (command.type) {
    case 'ADD_CONTAINER': {
      const cmd = command as AddContainerCommand;
      if (cmd.payload.parentContainerId) {
        const { element } = selectors.selectContainerById(state, cmd.payload.parentContainerId);
        if (!element) {
          return { valid: false, error: 'Parent container not found' };
        }
      }
      if (!cmd.payload.canvasId) {
        return { valid: false, error: 'Canvas ID is required' };
      }
      const canvas = selectors.selectCanvas(state, cmd.payload.canvasId);
      if (!canvas) {
        return { valid: false, error: 'Canvas not found' };
      }
      return { valid: true };
    }

    case 'REMOVE_CONTAINER': {
      const cmd = command as RemoveContainerCommand;
      const { element } = selectors.selectContainerById(state, cmd.payload.containerId);
      if (!element) {
        return { valid: false, error: 'Container not found' };
      }
      return { valid: true };
    }

    case 'UPDATE_CONTAINER': {
      const cmd = command as UpdateContainerCommand;
      const { element } = selectors.selectContainerById(state, cmd.payload.containerId);
      if (!element) {
        return { valid: false, error: 'Container not found' };
      }
      return { valid: true };
    }

    case 'MOVE_CONTAINER': {
      const cmd = command as MoveContainerCommand;
      const { element } = selectors.selectContainerById(state, cmd.payload.containerId);
      if (!element) {
        return { valid: false, error: 'Container not found' };
      }
      if (cmd.payload.toParentId) {
        const { element: parent } = selectors.selectContainerById(state, cmd.payload.toParentId);
        if (!parent) {
          return { valid: false, error: 'Target parent container not found' };
        }
      }
      return { valid: true };
    }

    case 'REORDER_CONTAINERS': {
      const cmd = command as ReorderContainersCommand;
      const canvas = selectors.selectCanvas(state, cmd.payload.canvasId);
      if (!canvas) {
        return { valid: false, error: 'Canvas not found' };
      }
      return { valid: true };
    }

    case 'DUPLICATE_CONTAINER': {
      const cmd = command as DuplicateContainerCommand;
      const { element } = selectors.selectContainerById(state, cmd.payload.sourceContainerId);
      if (!element) {
        return { valid: false, error: 'Source container not found' };
      }
      const canvas = selectors.selectCanvas(state, cmd.payload.canvasId);
      if (!canvas) {
        return { valid: false, error: 'Canvas not found' };
      }
      return { valid: true };
    }

    case 'ADD_ITEM': {
      const cmd = command as AddItemCommand;
      const { element } = selectors.selectContainerById(state, cmd.payload.containerId);
      if (!element) {
        return { valid: false, error: 'Container not found' };
      }
      return { valid: true };
    }

    case 'REMOVE_ITEM': {
      const cmd = command as RemoveItemCommand;
      const { element } = selectors.selectItemById(state, cmd.payload.itemId);
      if (!element) {
        return { valid: false, error: 'Item not found' };
      }
      return { valid: true };
    }

    case 'MOVE_ITEM': {
      const cmd = command as MoveItemCommand;
      const { element } = selectors.selectItemById(state, cmd.payload.itemId);
      if (!element) {
        return { valid: false, error: 'Item not found' };
      }
      const { element: fromContainer } = selectors.selectContainerById(state, cmd.payload.fromContainerId);
      if (!fromContainer) {
        return { valid: false, error: 'Source container not found' };
      }
      const { element: toContainer } = selectors.selectContainerById(state, cmd.payload.toContainerId);
      if (!toContainer) {
        return { valid: false, error: 'Target container not found' };
      }
      return { valid: true };
    }

    case 'UPDATE_ITEM': {
      const cmd = command as UpdateItemCommand;
      const { element } = selectors.selectItemById(state, cmd.payload.itemId);
      if (!element) {
        return { valid: false, error: 'Item not found' };
      }
      return { valid: true };
    }

    case 'REORDER_ITEMS': {
      const cmd = command as ReorderItemsCommand;
      const { element } = selectors.selectContainerById(state, cmd.payload.containerId);
      if (!element) {
        return { valid: false, error: 'Container not found' };
      }
      return { valid: true };
    }

    case 'BATCH': {
      const cmd = command as BatchCommand;
      for (const subCommand of cmd.payload.commands) {
        const result = validateCommand(state, subCommand);
        if (!result.valid) {
          return result;
        }
      }
      return { valid: true };
    }

    default:
      return { valid: false, error: `Unknown command type: ${(command as UndoableCommand).type}` };
  }
};
